import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3011';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses by redirecting to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('member');
      localStorage.removeItem('wallet');
      
      // Trigger auth change event
      window.dispatchEvent(new Event('auth-change'));
      
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Tournaments
export const tournamentAPI = {
  getAll: () => api.get('/tournaments'),
  getById: (id) => api.get(`/tournaments/${id}`),
  create: (data) => api.post('/tournaments', data),
  update: (id, data) => api.put(`/tournaments/${id}`, data),
  delete: (id) => api.delete(`/tournaments/${id}`),
  
  // Version management
  bumpVersion: (id, notes = '') => api.post(`/tournaments/${id}/version/bump`, { notes }),
  getVersionHistory: (id) => api.get(`/tournaments/${id}/version/history`),
  
  // Public access
  getPublicTeeSheet: (id, token = null) => {
    const url = `/tournaments/${id}/teesheet/view${token ? `?token=${token}` : ''}`;
    return api.get(url);
  },
  
  // Feedback submission
  submitFeedback: (id, comments, token = null) => {
    const url = `/tournaments/${id}/feedback${token ? `?token=${token}` : ''}`;
    return api.post(url, { comments });
  },
  
  // Feedback management
  getTournamentFeedback: (id, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);
    if (filters.playerGhin) params.append('playerGhin', filters.playerGhin);
    const url = `/tournaments/${id}/feedback${params.toString() ? `?${params.toString()}` : ''}`;
    return api.get(url);
  },
  
  updateFeedbackStatus: (feedbackId, status) => 
    api.put(`/tournaments/feedback/${feedbackId}/status`, { status }),
  
  bulkUpdateFeedbackStatus: (feedbackIds, status) => 
    api.put('/tournaments/feedback/bulk-status', { feedbackIds, status }),
};

// Players
export const playerAPI = {
  getAll: (tournamentId) => api.get(`/players/tournament/${tournamentId}`),
  getById: (id) => api.get(`/players/${id}`),
  create: (data) => api.post('/players', data),
  update: (id, data) => api.put(`/players/${id}`, data),
  delete: (id) => api.delete(`/players/${id}`),
  bulkImport: (tournamentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/players/tournament/${tournamentId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  reimport: (tournamentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/players/tournament/${tournamentId}/reimport`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  reprocessWithLLM: (tournamentId) =>
    api.post(`/players/tournament/${tournamentId}/reprocess-llm`),
  
  populatePaceData: (tournamentId) =>
    api.post(`/players/tournament/${tournamentId}/populate-pace-data`),

  // Refresh handicaps from Member records
  refreshHandicapsFromMembers: (tournamentId) =>
    api.post(`/players/tournament/${tournamentId}/refresh-handicaps`),

  analyzePlayerRequest: (playerName, originalRequest, allPlayerNames, tournamentType) =>
    api.post(`/players/analyze-request`, {
      playerName,
      originalRequest,
      allPlayerNames,
      tournamentType
    }),

  // Column mapping import
  previewExcel: (file, sampleRows = 5) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sampleRows', sampleRows);
    return api.post('/players/preview-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  importWithMapping: (tournamentId, file, columnMapping) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('columnMapping', JSON.stringify(columnMapping));
    return api.post(`/players/tournament/${tournamentId}/import-with-mapping`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Process player requests - parse originalPairingRequest into structured fields
  processRequests: (tournamentId, options = {}) =>
    api.post(`/players/tournament/${tournamentId}/process-requests`, options),

  // Preview player request changes (dry run - shows differences without applying)
  previewRequests: (tournamentId, options = {}) =>
    api.post(`/players/tournament/${tournamentId}/preview-requests`, options),

  // Apply only selected changes from preview
  applySelectedChanges: (tournamentId, selectedChanges) =>
    api.post(`/players/tournament/${tournamentId}/apply-changes`, { selectedChanges }),

  // Parse a single player's request
  parseSingleRequest: (playerId, options = {}) =>
    api.post(`/players/${playerId}/parse-request`, options),

  // ============================================
  // Roster Status Management
  // ============================================

  // Update roster status for a single player
  updateRosterStatus: (playerId, status, changedBy = 'user', reason = null) =>
    api.patch(`/players/${playerId}/roster-status`, { status, changedBy, reason }),

  // Bulk update roster status for multiple players
  bulkUpdateRosterStatus: (playerIds, status, changedBy = 'user', reason = null) =>
    api.post('/players/bulk-roster-status', { playerIds, status, changedBy, reason }),

  // Get status change history for a player
  getStatusHistory: (playerId) =>
    api.get(`/players/${playerId}/status-history`),

  // Get roster summary counts for a tournament
  getRosterSummary: (tournamentId) =>
    api.get(`/players/tournament/${tournamentId}/roster-summary`),
};

// Members
export const memberAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.membershipType) queryParams.append('membershipType', params.membershipType);
    if (params.tags) queryParams.append('tags', params.tags);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const url = `/members${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },
  
  getById: (id) => api.get(`/members/${id}`),
  create: (data) => api.post('/members', data),
  update: (id, data) => api.put(`/members/${id}`, data),
  delete: (id, hard = false) => api.delete(`/members/${id}${hard ? '?hard=true' : ''}`),
  
  // Special operations
  updateHandicap: (id, handicapIndex, source = 'manual') => 
    api.put(`/members/${id}/handicap`, { handicapIndex, source }),
  
  getStats: () => api.get('/members/stats'),
  
  bulkImport: (members, options = {}) => 
    api.post('/members/bulk-import', { members, options }),
  
  addToTournament: (memberId, tournamentId) => 
    api.post(`/members/${memberId}/add-to-tournament`, { tournamentId }),
  
  // Pace of play operations
  syncMemberPaceData: (memberId) => 
    api.post(`/members/${memberId}/sync-pace-data`),
  
  syncAllMembersPaceData: () =>
    api.post('/members/sync-all-pace-data'),

  getMemberPaceStats: (memberId) =>
    api.get(`/members/${memberId}/pace-stats`),
  
  // Import members from Excel file in backend/import folder
  importFromFile: (filename, options = {}) =>
    api.post('/members/import-from-file', { filename, options }),
  
  // Pace analytics (using member data, not bulk cache)
  getPaceAnalytics: () => 
    api.get('/members/pace-analytics'),
  
  // Wallet management for members
  createMemberWallet: (memberId, initialBalance = 0, description = '') =>
    api.post(`/members/${memberId}/wallet`, { initialBalance, description }),
  
  getMemberWallet: (memberId) =>
    api.get(`/members/${memberId}/wallet`),
  
  syncMemberWallet: (memberId) =>
    api.post(`/members/${memberId}/wallet/sync`),

  // ============================================
  // Member Portal Self-Service APIs
  // ============================================

  // Get portal summary (dashboard data)
  getPortalSummary: (memberId) =>
    api.get(`/members/${memberId}/portal-summary`),

  // Update profile (name, email, phone, address)
  updateProfile: (memberId, data) =>
    api.put(`/members/${memberId}/profile`, data),

  // Update preferences (time slots, partners, ride/walk)
  updatePreferences: (memberId, data) =>
    api.put(`/members/${memberId}/preferences`, data),

  // Update communication settings
  updateCommunication: (memberId, data) =>
    api.put(`/members/${memberId}/communication`, data),

  // Update last-minute invite preferences
  updateLastMinute: (memberId, data) =>
    api.put(`/members/${memberId}/last-minute`, data),

  // Search members for partner selection
  searchPartners: (query) =>
    api.get(`/members/search-partners?q=${encodeURIComponent(query)}`),

  // ============================================
  // Member Account Management (TD only)
  // ============================================

  // Create user account for a member (default password = lowercase lastname)
  createAccount: (memberId) =>
    api.post(`/members/${memberId}/create-account`),

  // Bulk create accounts for multiple members
  bulkCreateAccounts: (memberIds) =>
    api.post('/members/bulk-create-accounts', { memberIds }),

  // Reset member password to default
  resetPassword: (memberId) =>
    api.post(`/members/${memberId}/reset-password`),

};

// Pace Data (bulk cache operations)
export const paceDataAPI = {
  // Bulk sync from Balboa Park API
  syncAllPaceData: () =>
    api.post('/pace-data/sync-all'),
  
  // Get analytics from cached data
  getPaceAnalytics: () =>
    api.get('/pace-data/analytics'),
  
  // Get cache status and info
  getCacheStatus: () =>
    api.get('/pace-data/cache-status'),

  // Get lightweight summary for Dashboard (faster than full analytics)
  getPaceSummary: () =>
    api.get('/pace-data/summary'),

  // Get bulk pace data for specific member by GHIN
  getMemberBulkPaceData: (ghin) =>
    api.get(`/pace-data/member/${ghin}`),

  // Test/compare API endpoints
  testEndpoints: () =>
    api.get('/pace-data/test-endpoints'),

  // Balboa-style dashboard endpoints
  getRecentEvents: () =>
    api.get('/pace-data/recent-events'),

  getEventDetail: (eventKey) =>
    api.get(`/pace-data/event/${encodeURIComponent(eventKey)}`),

  getMembersList: (params = {}) =>
    api.get('/pace-data/members-list', { params }),

  getClubInsights: () =>
    api.get('/pace-data/club-insights'),

  // Get detailed member data with all rounds (for member detail view)
  getMemberDetail: (ghin) =>
    api.get(`/pace-data/member/${ghin}/detail`),
};

// Tee Sheets
export const teeSheetAPI = {
  getById: (id) => api.get(`/tee-sheet/${id}`),

  getByTournamentId: (tournamentId) => api.get(`/tee-sheet/tournament/${tournamentId}`),

  updateManualOverride: (playerId, overrideData) =>
    api.put(`/tee-sheet/manual-override/${playerId}`, overrideData),

  export: (id, format = 'pdf') =>
    api.get(`/tee-sheet/${id}/export?format=${format}`, {
      responseType: 'blob'
    }),

  addTeeTime: (groups, config = {}) =>
    api.post('/tee-sheet/add-tee-time', { groups, config }),

  save: (tournamentId, teeSheetData) =>
    api.put('/tee-sheet/save', { tournamentId, ...teeSheetData }),

  generateFromRules: (tournamentId) =>
    api.post('/tee-sheet/generate-from-rules', { tournamentId }),
};

// Merit and Pace Data
export const dataAPI = {
  getMeritData: (tournamentId) => api.get(`/merit-data/tournament/${tournamentId}`),
  getPaceData: (tournamentId) => api.get(`/pace-data/tournament/${tournamentId}`),
  uploadMerit: (tournamentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/merit-data/tournament/${tournamentId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadPace: (tournamentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/pace-data/tournament/${tournamentId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Wallets
export const walletAPI = {
  // Wallet management
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.ownerType) queryParams.append('ownerType', params.ownerType);
    if (params.active !== undefined) queryParams.append('isActive', params.active);
    if (params.includeArchived !== undefined) queryParams.append('includeArchived', params.includeArchived);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `/wallets${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },
  
  getById: (id) => api.get(`/wallets/${id}`),
  
  getByOwner: (ownerId, ownerType) => 
    api.get(`/wallets/owner/${ownerId}/${ownerType}`),
  
  create: (data) => api.post('/wallets', data),
  
  getBalance: (id) => api.get(`/wallets/${id}/balance`),
  
  getSummary: (id, dateFrom = null, dateTo = null) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    const url = `/wallets/${id}/summary${params.toString() ? `?${params.toString()}` : ''}`;
    return api.get(url);
  },
  
  // Transaction processing
  deposit: (walletId, amount, description, metadata = {}) =>
    api.post(`/wallets/${walletId}/deposit`, { amount, description, metadata }),
  
  withdraw: (walletId, amount, description, metadata = {}) =>
    api.post(`/wallets/${walletId}/withdraw`, { amount, description, metadata }),
  
  transfer: (fromWalletId, toWalletId, amount, description, metadata = {}) =>
    api.post('/transactions/transfer', { fromWalletId, toWalletId, amount, description, metadata }),
  
  // Transaction history
  getTransactionHistory: (walletId, options = {}) => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.type) params.append('type', options.type);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    
    const url = `/wallets/${walletId}/transactions${params.toString() ? `?${params.toString()}` : ''}`;
    return api.get(url);
  },
  
  // Transaction management
  getTransaction: (transactionId) => 
    api.get(`/transactions/${transactionId}`),
  
  reverseTransaction: (transactionId, reason) =>
    api.post(`/transactions/${transactionId}/reverse`, { reason }),
  
  validateTransaction: (transactionData) =>
    api.post('/transactions/validate', transactionData),
  
  // System reports
  getSystemStats: () => api.get('/reports/wallet-summary'),
  
  getWalletReport: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.ownerType) queryParams.append('ownerType', params.ownerType);
    
    const url = `/reports/wallet-report${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },
  
  getTransactionReport: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.type) queryParams.append('type', params.type);
    if (params.walletId) queryParams.append('walletId', params.walletId);
    
    const url = `/reports/transaction-report${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Archive management
  getArchivedWallets: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.ownerType) queryParams.append('ownerType', params.ownerType);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `/wallets/archived${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  archiveWallet: (walletId, reason = '') =>
    api.post(`/wallets/${walletId}/archive`, { reason }),

  unarchiveWallet: (walletId, reason = '') =>
    api.post(`/wallets/${walletId}/unarchive`, { reason }),

  // Club wallet management
  getClubWallet: () => api.get('/wallets/club'),
  
  createClubWallet: (clubName, initialBalance = 0, notes = '') =>
    api.post('/wallets/club', { clubName, initialBalance, notes })
};

// Contest Types
export const contestTypeAPI = {
  getAll: (active = true) => {
    const params = new URLSearchParams();
    if (active !== undefined) params.append('active', active);
    const url = `/contest-types${params.toString() ? `?${params.toString()}` : ''}`;
    return api.get(url);
  },
  
  getById: (id) => api.get(`/contest-types/${id}`),
  create: (data) => api.post('/contest-types', data),
  update: (id, data) => api.put(`/contest-types/${id}`, data),
  delete: (id) => api.delete(`/contest-types/${id}`),
};

// Contests
export const contestAPI = {
  // Tournament contest management
  getTournamentContests: (tournamentId) => 
    api.get(`/tournaments/${tournamentId}/contests`),
  
  createContest: (tournamentId, data) => 
    api.post(`/tournaments/${tournamentId}/contests`, data),
  
  // Individual contest operations
  getContest: (contestId) => api.get(`/contests/${contestId}`),
  updateContest: (contestId, data) => api.put(`/contests/${contestId}`, data),
  updateContestStatus: (contestId, status) => api.put(`/contests/${contestId}/status`, { status }),
  cancelContest: (contestId) => api.delete(`/contests/${contestId}`),
  
  // Contest financial operations
  addSubsidy: (contestId, amount, description) =>
    api.post(`/contests/${contestId}/subsidy`, { amount, description }),
  
  getAccounting: (contestId) => api.get(`/contests/${contestId}/accounting`),
  
  // Participant management
  addParticipant: (contestId, playerId, playerName) =>
    api.post(`/contests/${contestId}/participants`, { playerId, playerName }),
  
  processEntryFee: (contestId, playerId) =>
    api.post(`/contests/${contestId}/participants/${playerId}/pay`),
  
  removeParticipant: (contestId, playerId) =>
    api.delete(`/contests/${contestId}/participants/${playerId}`),
  
  // Payout management
  recordPayout: (contestId, payouts) =>
    api.post(`/contests/${contestId}/payouts`, { payouts }),
  
  // Status management
  updateStatus: (contestId, status) =>
    api.put(`/contests/${contestId}/status`, { status }),
};

// Email API
export const emailAPI = {
  // Get all templates
  getTemplates: () => api.get('/email-templates'),

  // Get template by ID with variables
  getTemplate: (id) => api.get(`/email-templates/${id}`),

  // Send email to member
  sendEmail: (data) => api.post('/email/send', data),

  // Get email history for member
  getMemberEmailHistory: (memberId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const url = `/members/${memberId}/emails${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Preview email with variables
  previewEmail: (templateId, variables) =>
    api.post(`/email-templates/${templateId}/preview`, { variables }),
};

// Email Template API (used by EmailNotificationEditModal)
export const emailTemplateAPI = {
  // Get all templates with optional filtering
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.active !== undefined) queryParams.append('active', params.active);
    if (params.category) queryParams.append('category', params.category);
    if (params.type) queryParams.append('type', params.type);

    const url = `/email-templates${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Get template by ID
  getById: (id) => api.get(`/email-templates/${id}`),

  // Create new template
  create: (data) => api.post('/email-templates', data),

  // Update template
  update: (id, data) => api.put(`/email-templates/${id}`, data),

  // Delete template
  delete: (id) => api.delete(`/email-templates/${id}`),

  // Preview template with variables
  preview: (id, variables) =>
    api.post(`/email-templates/${id}/preview`, { variables }),
};

// Scheduled Email Types API (email type blueprints with cascade support)
export const scheduledEmailTypeAPI = {
  // Get all email types
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.activeOnly !== undefined) queryParams.append('activeOnly', params.activeOnly);
    if (params.category) queryParams.append('category', params.category);

    const url = `/scheduled-email-types${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Get email type by ID
  getById: (id) => api.get(`/scheduled-email-types/${id}`),

  // Get email type by code
  getByCode: (code) => api.get(`/scheduled-email-types/code/${code}`),

  // Create new email type
  create: (data) => api.post('/scheduled-email-types', data),

  // Update email type (without cascade)
  update: (id, data) => api.put(`/scheduled-email-types/${id}`, data),

  // Update and cascade changes to existing notifications
  updateAndCascade: (id, data, options = {}) =>
    api.put(`/scheduled-email-types/${id}/cascade`, { ...data, ...options }),

  // Preview cascade changes before applying
  previewCascade: (id, data) =>
    api.post(`/scheduled-email-types/${id}/cascade/preview`, data),

  // Get statistics for an email type
  getStats: (id) => api.get(`/scheduled-email-types/${id}/stats`),

  // Soft delete (deactivate)
  delete: (id) => api.delete(`/scheduled-email-types/${id}`),

  // Hard delete (permanent)
  permanentDelete: (id) => api.delete(`/scheduled-email-types/${id}/permanent`),

  // Sync a notification back to its email type
  syncNotification: (typeId, notificationId, syncBy = 'admin') =>
    api.post(`/scheduled-email-types/${typeId}/sync-notification/${notificationId}`, { syncBy }),

  // ============================================
  // Notification Management
  // ============================================

  // Get a single notification by ID
  getNotificationById: (notificationId) =>
    api.get(`/scheduled-email-types/notifications/${notificationId}`),

  // Get notifications for an email type
  getNotifications: (typeId, options = {}) => {
    const queryParams = new URLSearchParams();
    if (options.status) queryParams.append('status', Array.isArray(options.status) ? options.status.join(',') : options.status);
    if (options.fromDate) queryParams.append('fromDate', options.fromDate);
    if (options.toDate) queryParams.append('toDate', options.toDate);
    if (options.limit) queryParams.append('limit', options.limit);
    if (options.skip) queryParams.append('skip', options.skip);
    if (options.sort) queryParams.append('sort', JSON.stringify(options.sort));

    const url = `/scheduled-email-types/${typeId}/notifications${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Regenerate notifications from calendar events
  regenerate: (typeId, options = {}) =>
    api.post(`/scheduled-email-types/${typeId}/regenerate`, options),

  // Skip individual notification
  skipNotification: (notificationId, reason = 'Manually skipped', skippedBy = 'admin') =>
    api.post(`/scheduled-email-types/notifications/${notificationId}/skip`, { reason, skippedBy }),

  // Unskip (re-enable) individual notification
  unskipNotification: (notificationId, unskippedBy = 'admin') =>
    api.post(`/scheduled-email-types/notifications/${notificationId}/unskip`, { unskippedBy }),

  // ============================================
  // Full Notification List Management
  // ============================================

  // Get ALL notifications with filters (across all email types)
  getAllNotifications: (options = {}) => {
    const queryParams = new URLSearchParams();
    if (options.status) queryParams.append('status', Array.isArray(options.status) ? options.status.join(',') : options.status);
    if (options.emailTypeId) queryParams.append('emailTypeId', options.emailTypeId);
    if (options.eventType) queryParams.append('eventType', options.eventType);
    if (options.eventId) queryParams.append('eventId', options.eventId);
    if (options.fromDate) queryParams.append('fromDate', options.fromDate);
    if (options.toDate) queryParams.append('toDate', options.toDate);
    if (options.search) queryParams.append('search', options.search);

    // Handle pagination - convert page to skip
    const limit = options.limit || 25;
    queryParams.append('limit', limit);
    if (options.page) {
      const skip = (options.page - 1) * limit;
      queryParams.append('skip', skip);
    } else if (options.skip) {
      queryParams.append('skip', options.skip);
    }

    // Handle sorting - convert sortBy/sortOrder to sort format
    if (options.sortBy) {
      const sortPrefix = options.sortOrder === 'desc' ? '-' : '';
      queryParams.append('sort', `${sortPrefix}${options.sortBy}`);
    } else if (options.sort) {
      queryParams.append('sort', options.sort);
    }

    const url = `/scheduled-email-types/all-notifications${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Update a single notification
  updateNotification: (notificationId, data) =>
    api.patch(`/scheduled-email-types/notifications/${notificationId}`, data),

  // Delete a single notification
  deleteNotification: (notificationId) =>
    api.delete(`/scheduled-email-types/notifications/${notificationId}`),

  // Preview a scheduled notification by ID (respects template overrides)
  previewScheduledNotification: (notificationId) =>
    api.get(`/scheduled-notifications/${notificationId}/preview`),

  // Bulk skip notifications for an email type
  bulkSkip: (typeId, options = {}) =>
    api.post(`/scheduled-email-types/${typeId}/bulk-skip`, options),

  // Bulk delete notifications for an email type
  bulkDeleteNotifications: (typeId, options = {}) => {
    const queryParams = new URLSearchParams();
    if (options.status) queryParams.append('status', Array.isArray(options.status) ? options.status.join(',') : options.status);
    if (options.fromDate) queryParams.append('fromDate', options.fromDate);
    if (options.toDate) queryParams.append('toDate', options.toDate);

    const url = `/scheduled-email-types/${typeId}/notifications${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.delete(url);
  },

  // ============================================
  // Event Selection for Notification Generation
  // ============================================

  // Get available events that can have notifications generated for this email type
  getAvailableEvents: (typeId, options = {}) => {
    const queryParams = new URLSearchParams();
    if (options.season) queryParams.append('season', options.season);
    if (options.futureOnly !== undefined) queryParams.append('futureOnly', options.futureOnly);

    const url = `/scheduled-email-types/${typeId}/available-events${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Generate notifications for selected events
  generateForEvents: (typeId, eventIds, options = {}) =>
    api.post(`/scheduled-email-types/${typeId}/generate-for-events`, {
      eventIds,
      replaceExisting: options.replaceExisting || false,
      createdBy: options.createdBy || 'admin'
    }),

  // ============================================
  // Archive and Resend Functions
  // ============================================

  // Archive a sent notification (allows resending)
  archiveNotification: (notificationId, reason = 'Archived for resend') =>
    api.post(`/scheduled-notifications/${notificationId}/archive`, {
      reason,
      archivedBy: 'admin'
    }),

  // Archive a sent notification and create a clone ready for resend
  archiveAndResend: (notificationId, options = {}) =>
    api.post(`/scheduled-notifications/${notificationId}/archive-and-resend`, {
      reason: options.reason || 'Archived for resend',
      archivedBy: options.archivedBy || 'admin',
      scheduledFor: options.scheduledFor,
      sendImmediately: options.sendImmediately || false
    }),

  // Get archived notifications
  getArchivedNotifications: (options = {}) => {
    const queryParams = new URLSearchParams();
    if (options.limit) queryParams.append('limit', options.limit);
    if (options.page) queryParams.append('page', options.page);
    if (options.eventId) queryParams.append('eventId', options.eventId);
    if (options.emailTypeId) queryParams.append('emailTypeId', options.emailTypeId);

    const url = `/scheduled-notifications/archived${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Send a pending notification immediately
  sendNow: (notificationId) =>
    api.post(`/scheduled-notifications/${notificationId}/send-now`),
};

// Season Events API (scheduled notifications)
export const seasonEventAPI = {
  // Get all events with optional filters
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.season) queryParams.append('season', params.season);
    if (params.status) queryParams.append('status', params.status);
    if (params.upcoming) queryParams.append('upcoming', params.upcoming);

    const url = `/season-events${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Get single event with notification history
  getById: (id) => api.get(`/season-events/${id}`),

  // Create new event
  create: (data) => api.post('/season-events', data),

  // Update event
  update: (id, data) => api.put(`/season-events/${id}`, data),

  // Delete event
  delete: (id) => api.delete(`/season-events/${id}`),

  // Get pending notifications
  getPendingNotifications: () => api.get('/season-events/notifications/pending'),

  // Get notification history
  getNotificationHistory: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.notificationType) queryParams.append('notificationType', params.notificationType);

    const url = `/season-events/notifications/history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Manually trigger a notification
  triggerNotification: (eventId, notificationType) =>
    api.post(`/season-events/${eventId}/trigger-notification`, { notificationType }),

  // Preview a notification email before sending
  previewNotification: (eventId, notificationType) =>
    api.post(`/season-events/${eventId}/preview-notification`, { notificationType }),

  // Send a test notification to a specific email
  testNotification: (eventId, notificationType, recipientEmail, templateName) =>
    api.post(`/season-events/${eventId}/test-notification`, { notificationType, recipientEmail, templateName }),

  // Skip a scheduled notification
  skipNotification: (eventId, notificationType, reason = '') =>
    api.post(`/season-events/${eventId}/skip-notification`, { notificationType, reason }),

  // Re-enable a skipped notification
  unskipNotification: (eventId, notificationType) =>
    api.post(`/season-events/${eventId}/unskip-notification`, { notificationType }),

  // Run daily notification check
  runDailyCheck: () => api.post('/season-events/notifications/run-check'),

  // Get cron service status
  getCronStatus: () => api.get('/season-events/cron/status'),

  // Update registration data (from scraper)
  updateRegistration: (id, data) => api.put(`/season-events/${id}/registration`, data),

  // Full event detail (360 view)
  getFullDetail: (id) => api.get(`/season-events/${id}/full`),

  // Activity timeline
  getActivity: (id, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.types) queryParams.append('types', params.types.join(','));
    const url = `/season-events/${id}/activity${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Revert an activity
  revertActivity: (eventId, activityId) =>
    api.post(`/season-events/${eventId}/activity/${activityId}/revert`),

  // Roster management
  getRoster: (id) => api.get(`/season-events/${id}/roster`),

  addRosterPlayer: (id, playerData) =>
    api.post(`/season-events/${id}/roster`, playerData),

  removeBalboaPlayer: (id, balboaPlayerId, balboaPlayerName, reason) =>
    api.post(`/season-events/${id}/roster/remove-balboa`, { balboaPlayerId, balboaPlayerName, reason }),

  removeRosterOverride: (eventId, overrideId) =>
    api.delete(`/season-events/${eventId}/roster/${overrideId}`),

  // Signups sync from Balboa
  syncSignups: (id) => api.post(`/season-events/${id}/sync-signups`),

  getSignups: (id) => api.get(`/season-events/${id}/signups`),

  // Validate signups against member database before tournament creation
  validateSignups: (id) => api.get(`/season-events/${id}/validate-signups`),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

// Cron Jobs API
export const cronJobAPI = {
  // Get overview status of all cron jobs
  getStatus: () => api.get('/cron-jobs/status'),

  // Health check endpoint
  getHealth: () => api.get('/cron-jobs/health'),

  // List all registered jobs
  getJobs: () => api.get('/cron-jobs/jobs'),

  // Get detailed status of a specific job
  getJob: (jobId) => api.get(`/cron-jobs/jobs/${jobId}`),

  // Manually trigger a job
  triggerJob: (jobId) => api.post(`/cron-jobs/jobs/${jobId}/trigger`),

  // Enable/start a job
  startJob: (jobId) => api.post(`/cron-jobs/jobs/${jobId}/start`),

  // Disable/stop a job
  stopJob: (jobId) => api.post(`/cron-jobs/jobs/${jobId}/stop`),

  // Update job schedule
  updateSchedule: (jobId, cronExpression) => api.patch(`/cron-jobs/jobs/${jobId}/schedule`, { cronExpression }),

  // Get execution history with optional filtering
  getExecutions: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.jobId) queryParams.append('jobId', params.jobId);
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);

    const url = `/cron-jobs/executions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Get specific execution details
  getExecution: (runId) => api.get(`/cron-jobs/executions/${runId}`),

  // Get failed executions
  getFailedExecutions: (limit = 50) => api.get(`/cron-jobs/failed?limit=${limit}`),

  // Get aggregated statistics
  getStats: (days = 30) => api.get(`/cron-jobs/stats?days=${days}`),

  // Get all job configurations
  getConfigs: () => api.get('/cron-jobs/configs'),

  // Update job configuration
  updateConfig: (jobId, data) => api.patch(`/cron-jobs/configs/${jobId}`, data),

  // Start all cron jobs
  startAll: () => api.post('/cron-jobs/start-all'),

  // Stop all cron jobs
  stopAll: () => api.post('/cron-jobs/stop-all'),

  // ============================================
  // Dynamic Job Management
  // ============================================

  // List all dynamic (user-created) jobs
  getDynamicJobs: () => api.get('/cron-jobs/dynamic'),

  // Create a new dynamic job
  createDynamicJob: (data) => api.post('/cron-jobs/dynamic', data),

  // Delete a dynamic job
  deleteDynamicJob: (jobId) => api.delete(`/cron-jobs/dynamic/${jobId}`),

  // Update a dynamic job
  updateDynamicJob: (jobId, data) => api.patch(`/cron-jobs/dynamic/${jobId}`, data),
};

// Tee Sheet Rules API
export const teeSheetRulesAPI = {
  // Get all rules for a tournament
  getRules: (tournamentId, filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.type) queryParams.append('type', filters.type);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.source) queryParams.append('source', filters.source);

    const url = `/tee-sheet-rules/tournament/${tournamentId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return api.get(url);
  },

  // Get a single rule by ID
  getRule: (ruleId) => api.get(`/tee-sheet-rules/${ruleId}`),

  // Extract rules from player data
  extractRules: (tournamentId, options = {}) =>
    api.post(`/tee-sheet-rules/tournament/${tournamentId}/extract`, options),

  // Create a new rule (director override)
  createRule: (tournamentId, ruleData) =>
    api.post(`/tee-sheet-rules/tournament/${tournamentId}`, ruleData),

  // Update a rule
  updateRule: (ruleId, updates) =>
    api.put(`/tee-sheet-rules/${ruleId}`, updates),

  // Delete a rule
  deleteRule: (ruleId) =>
    api.delete(`/tee-sheet-rules/${ruleId}`),

  // Get all conflicts for a tournament
  getConflicts: (tournamentId) =>
    api.get(`/tee-sheet-rules/tournament/${tournamentId}/conflicts`),

  // Resolve a conflict
  resolveConflict: (ruleId, conflictId, resolution) =>
    api.post(`/tee-sheet-rules/${ruleId}/conflicts/${conflictId}/resolve`, resolution),

  // Create a slot lock (pin player to specific position)
  createSlotLock: (tournamentId, data) =>
    api.post(`/tee-sheet-rules/tournament/${tournamentId}/slot-lock`, data),

  // Create a group lock (lock entire group together)
  createGroupLock: (tournamentId, data) =>
    api.post(`/tee-sheet-rules/tournament/${tournamentId}/group-lock`, data),

  // Get rules for a specific player
  getPlayerRules: (tournamentId, playerId) =>
    api.get(`/tee-sheet-rules/tournament/${tournamentId}/player/${playerId}`),

  // Get rule type constants
  getRuleTypes: () =>
    api.get('/tee-sheet-rules/types'),
};

// Portal Tournaments (Member-facing tournament views)
export const portalTournamentAPI = {
  getMyTournaments: () => api.get('/portal/tournaments'),
  getTournamentDetail: (id) => api.get(`/portal/tournaments/${id}`),
  getActivity: (id) => api.get(`/portal/tournaments/${id}/activity`),
  getRosterRequest: (id) => api.get(`/portal/tournaments/${id}/roster-request`),
  submitRosterRequest: (id, data) => api.put(`/portal/tournaments/${id}/roster-request`, data),
  getTeeSheet: (id) => api.get(`/portal/tournaments/${id}/tee-sheet`),
  getSideAction: (id) => api.get(`/portal/tournaments/${id}/side-action`),
  joinContest: (tournamentId, contestId) => api.post(`/portal/tournaments/${tournamentId}/contests/${contestId}/join`),
  getRecap: (id) => api.get(`/portal/tournaments/${id}/recap`),
  getMyFeedback: (id) => api.get(`/portal/tournaments/${id}/feedback`),
  submitFeedback: (id, data) => api.post(`/portal/tournaments/${id}/feedback`, data),
};

// ============================================
// Waitlist Management
// ============================================
export const waitlistAPI = {
  // Get ordered waitlist for a tournament
  getWaitlist: (tournamentId) =>
    api.get(`/waitlist/tournament/${tournamentId}`),

  // Add player(s) to waitlist
  addToWaitlist: (tournamentId, playerIds, reason = '') =>
    api.post(`/waitlist/tournament/${tournamentId}/add`, { playerIds, reason }),

  // Promote a player from waitlist to active roster
  promoteFromWaitlist: (tournamentId, playerId, reason = '') =>
    api.post(`/waitlist/tournament/${tournamentId}/promote/${playerId}`, { reason }),

  // Manually reorder the waitlist (TD override)
  reorderWaitlist: (tournamentId, orderedPlayerIds) =>
    api.post(`/waitlist/tournament/${tournamentId}/reorder`, { orderedPlayerIds }),

  // Get waitlist statistics
  getWaitlistStats: (tournamentId) =>
    api.get(`/waitlist/tournament/${tournamentId}/stats`),

  // Process a player drop and optionally auto-promote
  processDropAndPromote: (tournamentId, droppedPlayerId, autoPromote = true, dropReason = '') =>
    api.post(`/waitlist/tournament/${tournamentId}/process-drop`, {
      droppedPlayerId,
      autoPromote,
      dropReason
    }),

  // Remove a player from waitlist
  removeFromWaitlist: (tournamentId, playerId, reason = '') =>
    api.post(`/waitlist/tournament/${tournamentId}/remove/${playerId}`, { reason }),

  // Get fairness report (members frequently waitlisted)
  getFairnessReport: (season = null, minCount = 2) => {
    const params = new URLSearchParams();
    if (season) params.append('season', season);
    if (minCount) params.append('minCount', minCount);
    return api.get(`/waitlist/fairness-report?${params.toString()}`);
  }
};

export default api;